<template>
  <form
    @submit.prevent="submitForm"
    class="row"
  >
    <div class="col-xs-8">
      <div class="row">
        <section class="col-xs-12">
          <Box>
            <BaseInput
              label="ID / navn"
              v-model="info.name"
            />

            <BaseFormField label="Publiceringsdato">
              <Datejump v-model="info.publishDate">
                <BaseDatetimePicker v-model="info.publishDate" />
              </Datejump>
            </BaseFormField>

            <BaseFormField label="Status">
              <BaseCheckbox
                label="Slettet"
                v-model="info.deleted"
              />
            </BaseFormField>

            <BaseCheckbox
              label="Udsend notifikation"
              v-model="info.sendNotification"
            />

            <BaseTextarea
              label="Intern note"
              v-model="info.internalNote"
            />
          </Box>

          <Box>
            <BaseFormField class="select-wada">
              <h3>WADA klassicifering</h3>
              <BaseRadioButton
                :value="info.wadaClassification"
                name="selectWADA"
                store=""
                @input="info.wadaClassification = $event"
                label="Ingen klassicificering"
              />
              <BaseRadioButton
                v-for="(wada, wadaIndex) in getAllWADA"
                :key="wadaIndex"
                :value="info.wadaClassification"
                name="selectWADA"
                :store="wada.id"
                @input="info.wadaClassification = $event"
                :label="wada.name"
              />
            </BaseFormField>
          </Box>
        </section>
        <section class="col-xs-12">
          <Box>
            <RouteOfAdministrationStatuses :statuses="routeOfAdministrationStatuses" />
          </Box>

          <Box>
            <GlobalNotesSelector
              :notes="globalNotes"
              :current-date="this.info.publishDate"
            />
          </Box>

          <Box>
            <LocalNotes
              :notes="localNotes"
              @add="localNotes.push($event)"
              @remove="onRemoveLocalNote"
            />
          </Box>
        </section>
        <div class="col-xs-12">
          <BaseButton
            label="Gem indholdsstof"
            :disabled="loading"
            full
          />
          <BaseSpinner
            height="40px"
            width="40px"
            v-if="loading"
          />
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import BaseDatetimePicker from '../../BaseDatetimePicker.vue';
import Datejump from '../../Datejump.vue';
import RouteOfAdministrationStatuses from '../../RouteOfAdministrationStatuses.vue';
import LocalNotes from '../../LocalNotes.vue';
import GlobalNotesSelector from '../../GlobalNotesSelector.vue';
import BaseCheckbox from '../../BaseCheckbox.vue';
import BaseRadioButton from '../../BaseRadioButton.vue';
import Box from '../../Box.vue';
import BaseSpinner from '../../BaseSpinner.vue';
import BaseButton from '../../BaseButton.vue';
import BaseTextarea from '../../BaseTextarea.vue';
import BaseFormField from '../../BaseFormField.vue';
import BaseInput from '../../BaseInput.vue';
import SubstanceService from '../../../services/SubstanceService';
import DefaultDate from '../../../filters/DefaultDate';

export default {
    name: 'CreateSubstanceForm',
    components: {
        BaseInput,
        BaseFormField,
        BaseTextarea,
        BaseButton,
        BaseSpinner,
        Box,
        BaseRadioButton,
        BaseCheckbox,
        GlobalNotesSelector,
        LocalNotes,
        RouteOfAdministrationStatuses,
        Datejump,
        BaseDatetimePicker,
    },
    props: {
        defaultForm: { type: Object, default: () => {} },
    },
    data() {
        return {
            loading: false,
            form: {},
            info: {
                name: '',
                publishDate: window.moment().add(1, 'day').startOf('day').format(),
                deleted: false,
                wadaClassification: '',
                internalNote: '',
                sendNotification: true,
            },
            localNotes: [],
            globalNotes: [],
            routeOfAdministrationStatuses: [],
        };
    },
    computed: {
        ...mapGetters({
            getAllGlobalNotesFromDatetime: 'globalnotes/getAllGlobalNotesFromDatetime',
            getAllWADA: 'wada/getAllWADA',
            getAllRouteOfAdministration: 'routeofadministration/getAllRouteOfAdministration',
        }),
        mappedRouteOfAdministrationStatuses() {
            return this.routeOfAdministrationStatuses
                .filter(route => (route.statusCompetition > -1 && route.statusTraining > -1))
                .map(route => ({ routeOfAdministrationID: route.id, statusCompetition: route.statusCompetition, statusTraining: route.statusTraining }));
        },
        mappedGlobalNotes() {
            return this.globalNotes.filter(note => note.selected).map(note => ({ id: note.id }));
        },
        requestData() {
            return {
                info: { ...this.info, ...{ publishDate: DefaultDate(this.info.publishDate) } },
                localNotes: this.localNotes,
                globalNotes: this.mappedGlobalNotes,
                routeOfAdministrationStatuses: this.mappedRouteOfAdministrationStatuses,
            };
        },
    },
    methods: {
        ...mapActions({
            loadGlobalNotes: 'globalnotes/loadGlobalNotes',
            loadWADA: 'wada/loadWADA',
            loadRouteOfAdministration: 'routeofadministration/loadRouteOfAdministration',
        }),
        onRemoveLocalNote(localIndex) {
            this.localNotes = this.localNotes.filter((a, index) => index !== localIndex);
        },
        async submitForm() {
            try {
                this.loading = true;

                const payload = await SubstanceService.create(this.requestData);

                this.$snotify.success(`${this.info.name} er oprettet`);
                this.loading = false;
                this.$emit('created', payload);
            } catch (error) {
                this.loading = false;
            }
        },
        initGlobalNotes() {
            this.globalNotes = this.getAllGlobalNotesFromDatetime(this.info.publishDate).map(note => ({ ...note, selected: false })).filter(note => note.deleted === false);
        },
    },
    async created() {
        try {
            await this.loadGlobalNotes();
            await this.loadWADA();
            await this.loadRouteOfAdministration();

            this.routeOfAdministrationStatuses = this.getAllRouteOfAdministration.map(route => ({
                ...route, statusCompetition: -1, statusTraining: -1, updatedOnce: false,
            }));

            this.initGlobalNotes();

            // merge default attributes
            this.info = { ...this.info, ...this.defaultForm };
        } catch (error) {
            //
        }
    },
    watch: {
        'info.publishDate': function temp() { this.initGlobalNotes(); },
    },
};
</script>

<style lang="scss" scoped>
.select-wada /deep/ .form-field__control {
    display: flex;
    flex-direction: column;
}
</style>
